<template>
    <page-container maxSize="2xl">
        <div class="grid grid-cols-1 gap-6 xl:grid-cols-[auto_repeat(11,1fr)] xl:grid-rows-[auto_1fr]">
            <div class="order-2 xl:order-0 xl:row-span-2 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    hideCategoryFilter
                    hideResourceFilter
                    hideLockedSnippetsFilter
                    @reset="resetFilters()"
                    :minAllowedDate="minAllowedDate"
                ></analytics-sidebar>
            </div>

            <analytics-franchise-hub-page-header
                :backButtonHref="`/analytics/franchise-hub/${props.category}/${props.metric}`"
                backButtonText="Back to Metric"
                :fetchParams="debouncedFetchParams"
                :category="props.category"
                :metric="props.metric"
                pageTitle="Groups"
            />

            <div class="order-3 w-full relative xl:col-span-11">
                <transition
                    enterFromClass="opacity-0 -translate-y-4"
                    enterToClass="opacity-100 translate-y-0"
                    leaveFromClass="opacity-100 translate-y-0"
                    leaveToClass="opacity-0 -translate-y-4"
                >
                    <div v-if="loading" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    v-if="debouncedFetchParams"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': loading,
                        'opacity-100 delay-200 transition duration-150': !loading,
                    }"
                >
                    <analytics-category-metric-groups-table
                        :fetchParams="debouncedFetchParams"
                        :metric="props.metric"
                        :category="props.category"
                        :filename="`voxie-${props.category}-groups`"
                        :groupIds="props.groupIds"
                        :target="props.target"
                        :aboveTarget="props.aboveTarget"
                    />
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import { getDateRange, getMinAllowedDate, resourceTypesOptions, metricsWithGoals } from '~/utils/analytics';
import { audienceTypes } from '~/constants/message';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { analyticsCategoriesValues } from '~/constants/categories';
import LoadingBars from '~/components/general/LoadingBars.vue';
import AnalyticsCategoryMetricGroupsTable from './charts/time-series/AnalyticsCategoryMetricGroupsTable.vue';
import AnalyticsFranchiseHubPageHeader from '~/components/analytics/partial/AnalyticsFranchiseHubPageHeader.vue';

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    groupIds: {
        type: Array,
        required: false,
    },
    target: {
        type: Number,
        required: false,
    },
    aboveTarget: {
        type: Boolean,
        required: false,
    },
});

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
            resource_categories: undefined,
            resources: undefined,
        },
    };
};

const { fetchParams, loading, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const minAllowedDate = getMinAllowedDate(true);
</script>
